import { CUSTOM_FILTER_DELETED } from '../../../filterQueryFormat/UsageSubActionTypes';
import { USED_FILTERS_BAR } from '../../../filterQueryFormat/UsageActionTypes';
import { List, OrderedMap } from 'immutable';
import { isOperator } from '../../../filterQueryFormat/operator/Operator';
import { removeKeyPath } from '../../../filterQueryFormat/logic/LogicGroup';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQuickFiltersOnChangeHandler } from '../../../hooks/useQuickFiltersOnChangeHandler';
import DraggableQuickFilter, { CONTAINER_WIDTH } from './DraggableQuickFilter';
import FilterUserAction from '../../../filterQueryFormat/FilterUserAction';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
import { Button } from 'foundations-components/transitional/components/Button';
import { AddIcon, DeleteIcon, LeftIcon } from 'foundations-assets';
import styled from 'foundations-theming/utils';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import AddQuickFilterButton from './AddQuickFilterButton';
import { POPOVER_CONTENTS } from './MoreQuickFiltersButton';
import { Small } from 'foundations-components/transitional/components/Small';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const QuickFiltersComponentsContainer = styled.div.withConfig({
  displayName: "EditQuickFiltersPopoverContent__QuickFiltersComponentsContainer",
  componentId: "sb4cnm-0"
})(["display:flex;flex-wrap:wrap;gap:8px;min-width:400px;"]);
const getSelectedOptionsCount = condition => {
  if (!condition || !isOperator(condition)) {
    return 0;
  }
  if (Array.isArray(condition.values)) {
    return condition.values.length;
  }
  if (List.isList(condition.value)) {
    return condition.value.size;
  }
  return 1;
};
const EditQuickFiltersPopoverContent = ({
  buttonProperties,
  customPropertyNames,
  maxCustomQuickFilters,
  enableReordering,
  hasOverflowingQuickFilters,
  onCustomPropertyNamesChange,
  setCurrentPopoverContent
}) => {
  const {
    getTrackerInstance,
    props: {
      baseFilterFamily,
      onUserAction
    },
    quickFiltersInternalValue
  } = useContext(SegmentationFiltersInstanceContext);
  const [persistedPropertyNames, setPersistedPropertyNames] = useState(customPropertyNames);
  const onQuickFiltersChange = useQuickFiltersOnChangeHandler();
  const quickFiltersConditions = quickFiltersInternalValue ? quickFiltersInternalValue.conditions : OrderedMap();
  const isUnmounted = useRef(false);
  useEffect(() => {
    return () => {
      isUnmounted.current = true;
    };
  }, []);
  const onRemoveDeletedConditions = useCallback(deletedPropertyNames => {
    if (!quickFiltersInternalValue || deletedPropertyNames.length === 0) {
      return;
    }
    const newQuickFiltersInternalValue = quickFiltersConditions.entrySeq().reduce((acc, [key, value]) => {
      if (key && isOperator(value) && value.field && deletedPropertyNames.includes(value.field.name)) {
        const keyPath = List.of(key);
        return removeKeyPath(keyPath, acc);
      }
      return acc;
    }, quickFiltersInternalValue);
    if (quickFiltersInternalValue.equals(newQuickFiltersInternalValue)) {
      return;
    }
    onQuickFiltersChange(newQuickFiltersInternalValue);
  }, [onQuickFiltersChange, quickFiltersConditions, quickFiltersInternalValue]);
  useEffect(() => {
    return () => {
      if (isUnmounted.current) {
        const deletedPropertyNames = customPropertyNames.filter(propertyName => !persistedPropertyNames.includes(propertyName));
        onRemoveDeletedConditions(deletedPropertyNames);
        onCustomPropertyNamesChange(persistedPropertyNames);
      }
    };
  }, [customPropertyNames, onCustomPropertyNamesChange, onRemoveDeletedConditions, persistedPropertyNames]);
  const onDeleteQuickFilter = index => {
    var _getTrackerInstance;
    const newPersistedPropertyNames = [...persistedPropertyNames];
    const [deletedPropertyName] = newPersistedPropertyNames.splice(index, 1);
    setPersistedPropertyNames(newPersistedPropertyNames);
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('removedProperty', {
      primaryObjectTypeId: baseFilterFamily,
      version: '2',
      property: deletedPropertyName
    });
    onUserAction(FilterUserAction({
      action: USED_FILTERS_BAR,
      subAction: CUSTOM_FILTER_DELETED,
      filterFamily: baseFilterFamily,
      property: deletedPropertyName
    }));
  };
  const onDeleteAllQuickFilters = () => {
    setPersistedPropertyNames([]);
  };
  const onFinishReordering = property => {
    var _getTrackerInstance2;
    const quickFiltersContainer = document.querySelector('#edit-popover-quick-filters-container');
    if (!quickFiltersContainer) {
      return;
    }
    const newPersistedPropertyNames = [...quickFiltersContainer.querySelectorAll('.draggable-quick-filter')].map(el => el.id);
    setPersistedPropertyNames(newPersistedPropertyNames);
    (_getTrackerInstance2 = getTrackerInstance()) === null || _getTrackerInstance2 === void 0 || _getTrackerInstance2.track('reorderedProperty', {
      primaryObjectTypeId: baseFilterFamily,
      version: '2',
      property
    });
  };
  const onDragOver = evt => {
    evt.preventDefault();
    evt.stopPropagation();
    evt.dataTransfer.dropEffect = 'move';
    const quickFiltersContainer = document.querySelector('#edit-popover-quick-filters-container');
    const draggingItem = quickFiltersContainer === null || quickFiltersContainer === void 0 ? void 0 : quickFiltersContainer.querySelector('.dragging');
    if (!quickFiltersContainer || !draggingItem) {
      return;
    }

    // Get all items except the dragging item and make an array of them
    const siblings = [...quickFiltersContainer.querySelectorAll('.draggable-quick-filter:not(.dragging)')].filter(el => el instanceof HTMLElement);
    // Group siblings by row based on their vertical position
    const rows = [];
    let currentRow = [];
    let previousTop = null;
    siblings.forEach(sibling => {
      const rect = sibling.getBoundingClientRect();
      if (previousTop === null || rect.top === previousTop) {
        currentRow.push(sibling);
      } else {
        rows.push(currentRow);
        currentRow = [sibling];
      }
      previousTop = rect.top;
    });
    if (currentRow.length > 0) {
      rows.push(currentRow);
    }

    // Find the row the mouse is in
    const mouseY = evt.clientY;
    const targetRow = rows.find(row => {
      const rowRect = row[0].getBoundingClientRect();
      return mouseY >= rowRect.top - 10 && mouseY <= rowRect.bottom + 10;
    });
    if (!targetRow) {
      return;
    }

    // Find the sibling in the row that the mouse is before
    const mouseX = evt.clientX;
    const nextSibling = targetRow.find(sibling => {
      const rect = sibling.getBoundingClientRect();
      const siblingMiddleX = rect.left + sibling.offsetWidth / 2;
      return mouseX <= siblingMiddleX;
    });
    if (nextSibling) {
      // Insert before the found sibling
      quickFiltersContainer.insertBefore(draggingItem, nextSibling);
    } else {
      // Insert after the last item in the row
      const lastInRow = targetRow[targetRow.length - 1];
      quickFiltersContainer.insertBefore(draggingItem, lastInRow.nextSibling);
    }
  };
  const canAddQuickFilter = persistedPropertyNames.length < maxCustomQuickFilters;
  const showBackButton = hasOverflowingQuickFilters || customPropertyNames.length < maxCustomQuickFilters;
  return /*#__PURE__*/_jsxs(View, {
    as: "div",
    padding: "400",
    children: [!hasOverflowingQuickFilters && persistedPropertyNames.length === maxCustomQuickFilters && /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(Text, {
        color: "text-core-disabled",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.addQuickFilterDisabled"
        })
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      justifyContent: showBackButton ? 'space-between' : 'end',
      children: [showBackButton && /*#__PURE__*/_jsx(Button, {
        size: "sm",
        use: "transparent",
        onClick: () => setCurrentPopoverContent(''),
        padding: "0",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "100",
          children: [/*#__PURE__*/_jsx(LeftIcon, {
            size: "100",
            color: "icon-core-subtle"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.back"
          })]
        })
      }), /*#__PURE__*/_jsx(AddQuickFilterButton, {
        disabled: !canAddQuickFilter,
        setCurrentPopoverContent: setCurrentPopoverContent
      })]
    }), /*#__PURE__*/_jsx(Flex, {
      justifyContent: "space-between",
      alignItems: "center",
      marginBlock: "200",
      children: /*#__PURE__*/_jsx(Text, {
        typography: "heading-50",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.editQuickFilters"
        })
      })
    }), /*#__PURE__*/_jsxs(QuickFiltersComponentsContainer, {
      id: "edit-popover-quick-filters-container",
      onDragEnter: evt => evt.preventDefault(),
      onDragOver: onDragOver,
      children: [persistedPropertyNames.map((propertyName, index) => {
        const condition = quickFiltersConditions.find(quickFiltersCondition => isOperator(quickFiltersCondition) && !!quickFiltersCondition.field && quickFiltersCondition.field.name === propertyName);
        const selectedOptionsCount = getSelectedOptionsCount(condition);
        return /*#__PURE__*/_jsx(DraggableQuickFilter, {
          buttonProperties: buttonProperties,
          draggable: enableReordering && persistedPropertyNames.length > 1,
          onDeleteQuickFilter: () => onDeleteQuickFilter(index),
          onFinishReordering: onFinishReordering,
          propertyName: propertyName,
          selectedOptionsCount: selectedOptionsCount
        }, `${propertyName}-edit-popover-filter-component`);
      }), canAddQuickFilter && /*#__PURE__*/_jsx(Button, {
        size: "sm",
        use: "unstyled",
        onClick: () => setCurrentPopoverContent(POPOVER_CONTENTS.ADD_QUICK_FILTERS),
        children: /*#__PURE__*/_jsxs(Flex, {
          borderColor: "border-core-subtle",
          borderRadius: "200",
          borderStyle: "solid",
          borderWidth: "100",
          alignItems: "center",
          justifyContent: "center",
          gap: "200",
          paddingInline: "200",
          blockSize: `${buttonProperties.BUTTON_HEIGHT}px`,
          inlineSize: `${CONTAINER_WIDTH}px`,
          children: [/*#__PURE__*/_jsx(AddIcon, {
            color: "icon-core-subtle"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.addQuickFilterButton"
          })]
        })
      })]
    }), /*#__PURE__*/_jsx(View, {
      marginBlockStart: "100",
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.quickFiltersBar.customQuickFiltersCounter",
          options: {
            count: persistedPropertyNames.length,
            maxCount: maxCustomQuickFilters
          }
        })
      })
    }), /*#__PURE__*/_jsxs(Button, {
      use: "transparent",
      onClick: onDeleteAllQuickFilters,
      padding: "0",
      marginBlockStart: "300",
      children: [/*#__PURE__*/_jsx(DeleteIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.quickFiltersBar.deleteAllQuickFilters"
      })]
    })]
  });
};
export default EditQuickFiltersPopoverContent;